// ?tab=expenses&modal=expense-item&tpl=<id> (DD-045): one expense and everything done with it, in one drawer: change
// it, mark paid a bill that is missing its amount, stop it, delete it while none of its bills was paid (TD-099).
// Stopping and deleting confirm in the footer, never in a second drawer. The row menu's Stop and Delete open it at
// that step (&confirm=stop|delete). A stopped expense reads only, and can still be deleted; a bill of it still missing
// its amount is entered here too. Bills not paid yet follow a change; paid ones never change (TD-073).
import { useState } from "react";
import { Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { DayField } from "@/components/ui/DayField";
import { FooterConfirm, useFooterStep } from "@/components/ui/FooterConfirm";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { ModalPending } from "../components/ModalPending";
import { MissingLine, Required } from "../components/Required";
import { WithoutIva } from "../components/WithoutIva";
import { useDeleteTemplate, useExpenseTemplates, useMarkExpensePaid, useStopTemplate, useUpdateTemplate } from "../hooks/useExpenses";
import { useClientContracts, useServiceLines } from "../hooks/usePricing";
import { useClientSummaries } from "../hooks/useReports";
import { useSubmitOnce } from "../hooks/useSubmitOnce";
import { StaticNotice } from "../jobs/JobParts";
import type { ClientSummary, ExpenseCategory, ExpenseTemplate, MissingBill, ServiceLine } from "../types";
import {
  billCheck, cadenceText, deleteConfirm, expenseMissing, liveCategory, startStep, templateCheck, templateForm, templateInput, type TemplateForm,
  type TemplateStep,
} from "../utils/expenses";
import { dateLabel, monthLabel, verbAmount } from "../utils/format";
import { closeUnlessPending } from "../utils/mutations";
import { livePass, NO_PASS } from "../utils/passedCosts";
import { newRequestKey } from "../utils/requestKey";
import { CategoryField, ClientField, EXPENSE_LISTS_FAILED, PassAlongField, ServiceLineField } from "./ExpenseFields";

const EXPENSE = ENTITY_CONFIG.expense;
const expenseWord = EXPENSE.label.toLowerCase();
const STOP_LABEL = `Stop ${expenseWord}`;
const DELETE_LABEL = `Delete ${expenseWord}`;
const takeBack = "text-red-600 hover:text-red-700";

interface Props {
  templateId: string;
  /** The URL's confirm param: opened from the row menu's Stop or Delete, start at that confirm. */
  confirm: string | null;
  onClose: () => void;
}

export function ExpenseItemModal({ templateId, confirm, onClose }: Props) {
  const templates = useExpenseTemplates();
  const lines = useServiceLines();
  const clients = useClientSummaries();
  if (!templates.data || !lines.data || !clients.data) {
    return (
      <ModalPending
        size="lg"
        title={EXPENSE.label}
        failure={templates.isError || lines.isError || clients.isError ? EXPENSE_LISTS_FAILED : null}
        retrying={templates.isFetching || lines.isFetching || clients.isFetching}
        retry={() => {
          if (!templates.data) void templates.refetch();
          if (!lines.data) void lines.refetch();
          if (!clients.data) void clients.refetch();
        }}
        onClose={onClose}
      />
    );
  }
  const template = templates.data.templates.find((t) => t.id === templateId);
  if (!template) {
    return (
      <StaticNotice
        size="lg"
        title={`${EXPENSE.label} not found`}
        message={`This ${expenseWord} no longer exists. The current ones are on the ${EXPENSE.plural} tab.`}
        onClose={onClose}
      />
    );
  }
  return (
    <ItemForm
      template={template}
      categories={templates.data.categories}
      lines={lines.data}
      clients={clients.data.clients}
      today={templates.data.today}
      confirm={confirm}
      onClose={onClose}
    />
  );
}

type Fields = Omit<TemplateForm, "pending">;

interface FormProps {
  template: ExpenseTemplate;
  categories: ExpenseCategory[];
  lines: ServiceLine[];
  clients: ClientSummary[];
  today: string;
  confirm: string | null;
  onClose: () => void;
}

function ItemForm({ template: live, categories, lines, clients, today, confirm, onClose }: FormProps) {
  const update = useUpdateTemplate();
  const stop = useStopTemplate();
  const remove = useDeleteTemplate();
  // Owned here, not by the bill block, so the modal stays open while a Mark paid is in flight.
  const markPaid = useMarkExpensePaid();
  // A save or a stop writes its answer into the list before this modal closes on it: until then the modal reads the
  // expense as it was asked about, never flashing Stopped.
  const [held, setHeld] = useState<ExpenseTemplate | null>(null);
  const template = held ?? live;
  const stopped = template.endsOn !== null;
  const [form, setForm] = useState<Fields>(() => templateForm(template));
  const footerStep = useFooterStep<TemplateStep>(startStep(confirm, template));
  const pending = update.isPending || stop.isPending || remove.isPending || markPaid.isPending;
  // A category deleted meanwhile drops from the pick, so Save asks for another instead of being refused again.
  const category = liveCategory(form.category, categories);
  const contractsQuery = useClientContracts(form.clientId || undefined);
  const own = contractsQuery.data?.ownClient ?? false;
  // The agency's own client is never billed (TD-105): the checks and the request read the box as off.
  const fields = { ...form, pass: livePass(form.pass, own), category, pending };
  const check = templateCheck(fields);
  const waitsFor = expenseMissing({ ...form, category }, false);
  const patch = (p: Partial<Fields>) => setForm((f) => ({ ...f, ...p }));
  const stopMonth = monthLabel(today.slice(0, 7));
  const sendOnce = useSubmitOnce();
  // The step the footer confirms, while the expense still allows it (a refetch can take it away).
  const step = startStep(footerStep.step, template);
  // The bill block goes once its own Mark paid lands; the refetch brings the next missing bill, if any.
  const missing = template.missing && !(markPaid.isSuccess && markPaid.variables?.id === template.missing.id) ? template.missing : null;
  // Closes on the answer; a refusal (toasted by the mutation, the data refetched) clears its confirm, so it never
  // offers itself again, and focus goes back to the action that asked.
  const send = (request: () => Promise<unknown>) =>
    sendOnce(
      () => {
        setHeld(template);
        return request();
      },
      () => onClose(),
      () => {
        setHeld(null);
        footerStep.leave();
      },
    );
  const save = () => send(() => update.mutateAsync({ id: template.id, input: templateInput(fields) }));
  const doStop = () => send(() => stop.mutateAsync(template.id));
  const doDelete = () => send(() => remove.mutateAsync(template.id));
  const deleteButton = template.deletable && (
    <Button
      variant="ghost"
      className={`whitespace-nowrap ${takeBack}`}
      onClick={() => footerStep.ask("delete")}
      disabled={pending}
      autoFocus={footerStep.returnsTo("delete")}
    >
      {DELETE_LABEL}
    </Button>
  );

  const footer =
    step === "delete" ? (
      <FooterConfirm confirmLabel={DELETE_LABEL} onConfirm={doDelete} onKeep={footerStep.leave} pending={pending} loading={remove.isPending}>
        {deleteConfirm(template.label)}
      </FooterConfirm>
    ) : stopped ? (
      // Stopped, the delete is the one step left. With none, there is no footer: the X closes (J26).
      deleteButton || null
    ) : step === "stop" ? (
      <FooterConfirm confirmLabel={STOP_LABEL} onConfirm={doStop} onKeep={footerStep.leave} pending={pending} loading={stop.isPending}>
        Stop after {stopMonth}? Bills up to it stay.{template.cadence !== "monthly" ? ` Its later months count in ${stopMonth}.` : ""}
      </FooterConfirm>
    ) : (
      <>
        {/* Stacked on a phone, so the two take-backs and Save fit one footer. */}
        <div className="mr-auto flex flex-col items-start sm:flex-row sm:items-center gap-1">
          <Button
            variant="ghost"
            className={`whitespace-nowrap ${takeBack}`}
            onClick={() => footerStep.ask("stop")}
            disabled={pending}
            autoFocus={footerStep.returnsTo("stop")}
          >
            {STOP_LABEL}
          </Button>
          {deleteButton}
        </div>
        <Button onClick={save} disabled={check.blocked} isLoading={update.isPending}>Save</Button>
      </>
    );

  return (
    <FinanceDrawer onClose={closeUnlessPending(pending, onClose)} size="lg" title={template.label} footer={footer}>
      <div className="space-y-4">
        {stopped && <Callout tone="gray">Stopped after {monthLabel((template.endsOn ?? today).slice(0, 7))}. Its bills stay in Money and Months.</Callout>}
        {/* A stopped expense keeps its bills up to the stop: one still missing its amount is entered here too. */}
        {missing && <MissingBillBlock key={missing.id} bill={missing} currency={template.currency} today={today} markPaid={markPaid} busy={pending} />}
        <p className="text-sm text-gray-600">
          {cadenceText(template)}, from {dateLabel(template.startsOn)}.{template.nextBill ? ` Next bill ${dateLabel(template.nextBill)}.` : ""}
        </p>
        <fieldset disabled={stopped} className="space-y-4 disabled:opacity-60">
          <FormField label={<Required>What it is</Required>} error={check.labelError}>
            <input className={inputClassName} aria-required="true" value={form.label} onChange={(e) => patch({ label: e.target.value })} />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label={form.varies ? "Amount" : <Required>Amount</Required>} hint={form.varies ? "Each bill's amount is entered when it is marked paid" : "Bills not paid yet follow it. Paid ones never change"} error={check.amountError}>
              {form.varies ? (
                <div className="h-9 flex items-center text-sm text-gray-500">Varies</div>
              ) : (
                <>
                  <MoneyInput valueMinor={form.amount} currency={template.currency} onChange={(amount) => patch({ amount })} invalid={!!check.amountError} ariaLabel="Amount" required />
                  <WithoutIva />
                </>
              )}
            </FormField>
            <FormField label="Day of the month" error={check.dayError}>
              <input type="number" min={1} max={28} className={inputClassName} value={form.day} onChange={(e) => patch({ day: Number(e.target.value) })} />
            </FormField>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" checked={form.varies} onChange={(e) => patch({ varies: e.target.checked })} />
            The amount varies each time
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CategoryField categories={categories} value={category} onChange={(picked) => patch({ category: picked })} error={check.categoryError} />
            <ClientField clients={clients} value={form.clientId} onChange={(clientId) => patch({ clientId, pass: clientId ? form.pass : NO_PASS })} />
            <ServiceLineField lines={lines} value={form.serviceLineId} onChange={(serviceLineId) => patch({ serviceLineId })} />
          </div>
          <PassAlongField clientId={form.clientId} own={own} value={form.pass} onChange={(pass) => patch({ pass })} markupError={check.markupError} />
        </fieldset>
        {!stopped && <MissingLine button="Save" missing={waitsFor} />}
      </div>
    </FinanceDrawer>
  );
}

type MarkPaid = ReturnType<typeof useMarkExpensePaid>;

interface BillProps {
  bill: MissingBill;
  currency: string;
  today: string;
  markPaid: MarkPaid;
  /** Any of the modal's requests in flight, this one's included. */
  busy: boolean;
}

/** The oldest bill of a varying expense that is due and has no amount yet: marked paid here, with the amount on it
 * (TD-077). Its own request key; keyed by the bill, so the next missing one starts fresh. */
function MissingBillBlock({ bill, currency, today, markPaid, busy }: BillProps) {
  const [amount, setAmount] = useState<number | null>(null);
  // J36: a bill is marked paid on the day it is recorded unless the owner picks another.
  const [paidOn, setPaidOn] = useState(today);
  const [requestId] = useState(newRequestKey);
  const check = billCheck({ amount, paidOn, pending: busy }, today);
  // What Mark paid waits for, in form order.
  const waitsFor = [...(amount ? [] : ["the amount on the bill"]), ...(paidOn ? [] : ["the payment date"])];
  const send = useSubmitOnce();
  const submit = () => send(() => markPaid.mutateAsync({ id: bill.id, input: { amountMinor: amount ?? 0, paidOn }, requestId }));
  return (
    <div className="rounded-lg bg-amber-50 px-3 py-3 space-y-2">
      <div className="text-sm font-medium text-amber-900">{monthLabel(bill.accrualMonth)} bill, dated {dateLabel(bill.date)}: amount missing</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:items-end">
        <FormField label={<Required>Amount on the bill</Required>} error={amount !== null ? check.amountError : undefined}>
          <MoneyInput valueMinor={amount} currency={currency} onChange={setAmount} invalid={amount !== null && !!check.amountError} ariaLabel="Amount on the bill" required />
        </FormField>
        <FormField label={<Required>Paid on</Required>} error={check.dateError}>
          {/* finance-real (the user 2026-10-23): a single day is DayField, the calendar every finance date uses. */}
          <DayField ariaLabel="Paid on" required max={today} value={paidOn} onChange={setPaidOn} />
        </FormField>
        <Button onClick={submit} disabled={check.blocked} isLoading={markPaid.isPending}>{verbAmount("Mark paid", amount, currency)}</Button>
      </div>
      {/* Under the row, not the field: inside it the date and Mark paid, bottom-aligned, would sit below the amount. */}
      <WithoutIva />
      <MissingLine button="Mark paid" missing={waitsFor} />
    </div>
  );
}
